import { client } from './client';
import { dotCache, getCacheKey } from './cacheService';
import { DEFAULT_LANGUAGE_ID } from './constants';
import { buildContentTypeQueryConfig } from './content-type-query-builder';

/**
 * Check if experiments are enabled
 * @return {boolean}
 */
const isExperimentsEnabled = () => {
    return !!process.env.NEXT_PUBLIC_EXPERIMENTS_API_KEY;
};

/**
 * Extract values from objects and create a string representation
 * @param {*} value - Value to extract
 * @return {string}
 */
const extractValueToString = (value) => {
    if (value === null || value === undefined) {
        return '';
    }

    if (typeof value === 'object') {
        if (Array.isArray(value)) {
            return value.map((item) => extractValueToString(item)).join(',');
        }
        return JSON.stringify(value);
    }

    return String(value);
};

/**
 * Generate cache key from query parameters
 * @param {Object} params - Query parameters
 * @return {string}
 */
const generateCacheKey = (params) => {
    const { contentType = '' } = params;

    const keyComponents = Object.values(params).map((value) => extractValueToString(value));

    const keyString = keyComponents.join('|');
    const hash = getCacheKey(keyString);

    return `query_${contentType}_${hash}`;
};

/**
 * Universal function to build getCollection queries with caching support.
 * Included experiments create a double API call, this function allows you to avoid this.
 * The function will return a promise that will resolve to the result of the query.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.contentType - Content type name
 * @param {Object} options.queryConfig - Query configuration
 * @param {Object} options.queryConfig.sortBy - Sort configuration (default: { field: 'modDate', order: 'desc' })
 * @param {number} options.limit - Number of items to return
 * @param {number} options.page - Page number
 * @param {Object} options.filter - Filter object
 * @param {string} options.searchTerm - Search term
 * @param {string} options.tagTerm - Tag term
 * @param {string} options.defaultQuery - Default query string
 * @param {string} options.searchQuery - Search query string
 * @param {string} options.cacheKeyPrefix - Optional cache key prefix
 * @param {number} options.depth - Depth for loading related objects
 * @param {string} options.language - Language ID for content
 * @return {Object} Built query object
 */
const ongoingRequests = new Map();

export const buildCollectionWithCache = async ({
    contentType,
    queryConfig = {},
    limit,
    page,
    filter = {},
    searchTerm,
    tagTerm,
    defaultQuery,
    searchQuery,
    cacheKeyPrefix = 'collection',
    depth,
    appendFilter,
    language,
    conhostId
}) => {
    const params = {
        contentType,
        filter,
        page,
        limit,
        tagTerm,
        searchTerm,
        depth,
        language,
        queryConfig: {
            ...queryConfig,
            defaultQuery,
            searchQuery
        }
    };
    const cacheKey = `${cacheKeyPrefix}_${generateCacheKey(params)}`;

    if (isExperimentsEnabled()) {
        const cachedResult = dotCache.get(cacheKey);

        if (cachedResult) return cachedResult;
        if (ongoingRequests.has(cacheKey)) return await ongoingRequests.get(cacheKey);
    }
    const contentTypeQueryConfig = buildContentTypeQueryConfig(
        contentType,
        conhostId,
        defaultQuery || queryConfig?.defaultQuery || ''
    );

    const finalSortBy = queryConfig?.sortBy || contentTypeQueryConfig.sortBy;
    // Content types stored in SYSTEM_HOST need includeSystemHost() so the SDK groups
    // the injected +conhost:siteId with SYSTEM_HOST instead of scoping only to the site.
    const SYSTEM_HOST_TYPES = ['Announcement', 'Contributor'];
    let constructedQuery = client.content
        .getCollection(contentType)
        .sortBy([finalSortBy])
        .limit(limit || 10)
        .page(page || 1);

    if (SYSTEM_HOST_TYPES.includes(contentType)) {
        constructedQuery = constructedQuery.includeSystemHost();
    }

    if (depth !== undefined) {
        constructedQuery = constructedQuery.depth(depth);
    }

    if (language !== undefined) {
        constructedQuery = constructedQuery.language(language);
    } else {
        constructedQuery = constructedQuery.language(DEFAULT_LANGUAGE_ID);
    }

    // Build the complete raw query in a single .query() call.
    // Each call to .query(string) replaces the previous value, so we must
    // combine all constraints (base, search, tag, filters) into one string.
    //
    // Always use the full query (including content-type visibility constraints like
    // +Blog.showInListing:true, +Video.show:true, +Podcast.show:true, etc.)
    // regardless of whether filters/search/tag are active. This ensures that
    // content marked as hidden is never surfaced in any context.
    const filterParts = appendFilter ? buildFilterParts(filter, contentType) : [];
    const queryToUse = contentTypeQueryConfig.query;

    let rawQueryBase = '';

    if (searchTerm || tagTerm) {
        rawQueryBase = [
            queryToUse,
            searchTerm ? `+title:${searchTerm}*` : '',
            tagTerm ? `+${contentType}.tags:"${tagTerm}"*` : ''
        ]
            .filter(Boolean)
            .join(' ');
    } else if (queryToUse) {
        rawQueryBase = queryToUse;
    }

    const finalRawQuery = [rawQueryBase, ...filterParts].filter(Boolean).join(' ');

    if (finalRawQuery.trim()) {
        constructedQuery = constructedQuery.query(finalRawQuery);
    }
    const requestPromise = constructedQuery
        .then(async (result) => {
            if (isExperimentsEnabled()) {
                dotCache.set(cacheKey, result);
            }
            ongoingRequests.delete(cacheKey);
            return result;
        })
        .catch((error) => {
            ongoingRequests.delete(cacheKey);
            throw error;
        });

    if (isExperimentsEnabled()) {
        ongoingRequests.set(cacheKey, requestPromise);
    }

    return await requestPromise;
};

/**
 * Wrap a Lucene filter value in double quotes if it contains whitespace or special characters.
 * @param {string} val
 * @return {string}
 */
const quoteIfNeeded = (val) => {
    const str = String(val);
    return /[\s+\-!(){}[\]^"~*?:\\/]/.test(str) ? `"${str.replace(/"/g, '\\"')}"` : str;
};

/**
 * Build filter condition strings from a filters object.
 * Returns an array of Lucene query parts — does NOT mutate the builder.
 *
 * @param {Object} filters
 * @param {string} contentType
 * @return {string[]}
 */
const buildFilterParts = (filters, contentType) => {
    const filterQueries = [];

    Object.keys(filters).forEach((key) => {
        const { value, field } = normalizeFilter({ filters, key });

        if (value === '*' || !value || (Array.isArray(value) && value.length === 0)) {
            return;
        }

        if (Array.isArray(value)) {
            const qualifiedField = field.includes('.') ? field : `${contentType}.${field}`;
            const orQuery = value.map((val) => `${qualifiedField}:${quoteIfNeeded(val)}`).join(' ');
            filterQueries.push(`+(${orQuery})`);
        } else {
            const qualifiedField = field.includes('.') ? field : `${contentType}.${field}`;
            filterQueries.push(`+${qualifiedField}:${quoteIfNeeded(value)}`);
        }
    });

    return filterQueries;
};

/**
 * Normalize the filter to be used in the query
 *
 * Converts the filter object to a field/value pair
 *
 * @param {*} { filters, key }
 * @return {*}
 */
const normalizeFilter = ({ filters, key }) => {
    const value = filters[key];

    if (typeof value !== 'object') {
        return {
            value,
            field: key
        };
    }

    return value;
};
